import {
  InventoryModule,
  PermissionCheck,
  PermissionType,
  RoleAuthorizationState,
  RolePermission
} from 'models/permission.model';
import { STORAGE_KEYS } from './constants';

type LoginRolePayload =
  | string
  | {
      id?: string;
      name?: string;
      role?: string;
      type?: string;
      role_type?: string;
      permissions?: PermissionType[];
    };

const ACTION_IMPLICATIONS: Record<string, string[]> = {
  [RolePermission.Read]: [RolePermission.Read],
  [RolePermission.Write]: [RolePermission.Write, RolePermission.Read]
};

const MODULE_IMPLICATIONS: Record<string, string[]> = {
  transfers: ['main_store_transfer', 'kitchen_store_transfer']
};

const LEGACY_WRITE_ACTIONS = new Set(['approve', 'submit', 'manage']);

const normalizePermissionAction = (value?: string) => {
  const action = normalizeToken(value);
  if (action === RolePermission.Read) return RolePermission.Read;
  if (action === RolePermission.Write || LEGACY_WRITE_ACTIONS.has(action)) {
    return RolePermission.Write;
  }
  return action;
};

export const VIEW_PERMISSION_ACTIONS: RolePermission[] = [
  RolePermission.Read,
  RolePermission.Write
];

export const EDIT_PERMISSION_ACTIONS: RolePermission[] = [RolePermission.Write];

export const SUBMIT_PERMISSION_ACTIONS: RolePermission[] = [RolePermission.Write];

const normalizeToken = (value?: string) => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  if (normalized === 'inventory_managment') {
    return 'inventory_management';
  }

  return normalized;
};

const normalizePermission = (permission: PermissionType): PermissionType => ({
  ...permission,
  module: normalizeToken(permission.module),
  action: normalizePermissionAction(permission.action)
});

export const normalizePermissions = (permissions?: PermissionType[]): PermissionType[] => {
  if (!Array.isArray(permissions)) return [];

  const unique = new Map<string, PermissionType>();
  permissions.forEach((permission) => {
    if (!permission?.module || !permission?.action) return;
    const normalized = normalizePermission(permission);
    const key = `${normalized.module}:${normalized.action}`;
    if (!unique.has(key)) unique.set(key, normalized);
  });

  return Array.from(unique.values());
};

export type PermissionIndex = Record<string, Set<string>>;

export const buildPermissionIndex = (permissions?: PermissionType[]): PermissionIndex => {
  const normalized = normalizePermissions(permissions);
  const index: PermissionIndex = {};

  normalized.forEach((permission) => {
    const moduleKey = normalizeToken(permission.module);
    const moduleKeys = [moduleKey, ...(MODULE_IMPLICATIONS[moduleKey] ?? [])];
    const actionKey = normalizePermissionAction(permission.action);
    const actions = ACTION_IMPLICATIONS[actionKey] ?? [actionKey];

    moduleKeys.forEach((resolvedModuleKey) => {
      if (!index[resolvedModuleKey]) {
        index[resolvedModuleKey] = new Set<string>();
      }
      actions.forEach((action) => index[resolvedModuleKey].add(action));
    });
  });

  return index;
};

export const canWithIndex = (
  permissionIndex: PermissionIndex,
  module: InventoryModule | string,
  actions: RolePermission | RolePermission[] = RolePermission.Read,
  options?: { match?: 'any' | 'all' }
) => {
  const moduleKey = normalizeToken(module);
  const moduleActions = permissionIndex[moduleKey];
  if (!moduleActions?.size) return false;

  const checkActions = (Array.isArray(actions) ? actions : [actions]).map((action) =>
    normalizePermissionAction(action)
  );
  if (!checkActions.length) return false;

  const match = options?.match ?? 'any';
  if (match === 'all') {
    return checkActions.every((action) => moduleActions.has(action));
  }
  return checkActions.some((action) => moduleActions.has(action));
};

export const hasPermission = (
  permissions: PermissionType[],
  module: InventoryModule | string,
  actions: RolePermission | RolePermission[] = RolePermission.Read,
  options?: { match?: 'any' | 'all' }
) => {
  return canWithIndex(buildPermissionIndex(permissions), module, actions, options);
};

export const hasModuleAccess = (permissions: PermissionType[], module: InventoryModule | string) =>
  hasPermission(permissions, module, VIEW_PERMISSION_ACTIONS);

export const canAnyPermissionCheck = (
  permissionIndex: PermissionIndex,
  checks: PermissionCheck[] = []
) => {
  return checks.some((check) =>
    canWithIndex(permissionIndex, check.module, check.actions ?? RolePermission.Read, check.options)
  );
};

const isRoleObject = (
  value: LoginRolePayload
): value is {
  id?: string;
  name?: string;
  role?: string;
  type?: string;
  role_type?: string;
  permissions?: PermissionType[];
} => {
  return typeof value === 'object' && value !== null;
};

export const extractRoleAuthorization = (payload: any): RoleAuthorizationState => {
  const rolePayload = payload?.role as LoginRolePayload | undefined;
  const inlinePermissions = payload?.user?.permissions;

  let roleName: string | undefined;
  let roleId: string | undefined;
  let rolePermissions: PermissionType[] = [];

  if (typeof rolePayload === 'string') {
    roleName = rolePayload;
  } else if (rolePayload && isRoleObject(rolePayload)) {
    roleName = rolePayload.name;
    roleId = rolePayload.id;
    rolePermissions = rolePayload.permissions ?? [];
  }

  if (!rolePermissions.length && Array.isArray(inlinePermissions)) {
    rolePermissions = inlinePermissions;
  }
  return {
    roleName,
    roleId,
    permissions: normalizePermissions(rolePermissions)
  };
};

export type StoredRole = {
  name?: string;
  id?: string;
  permissions?: PermissionType[];
};

const parseStoredRole = (value: unknown): StoredRole | undefined => {
  if (!value) return undefined;

  if (typeof value === 'string') {
    return { name: value };
  }

  if (typeof value === 'object' && value !== null) {
    const roleValue = value as Record<string, unknown>;
    const name = String(
      roleValue.name ?? roleValue.role ?? roleValue.role_name ?? roleValue.type ?? ''
    ).trim();
    const id = String(roleValue.id ?? roleValue.role_id ?? roleValue.roleId ?? '').trim();
    const permissions = normalizePermissions(
      Array.isArray(roleValue.permissions) ? (roleValue.permissions as PermissionType[]) : []
    );

    return {
      name: name || undefined,
      id: id || undefined,
      permissions
    };
  }

  return undefined;
};

export const readStoredRole = (): StoredRole | undefined => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.ROLE);
    if (!raw) return undefined;

    try {
      return parseStoredRole(JSON.parse(raw));
    } catch {
      return parseStoredRole(raw);
    }
  } catch {
    return undefined;
  }
};

export const writeStoredRole = (role?: StoredRole) => {
  if (!role?.name && !role?.id && !(role?.permissions && role.permissions.length)) {
    localStorage.removeItem(STORAGE_KEYS.ROLE);
    return;
  }

  localStorage.setItem(
    STORAGE_KEYS.ROLE,
    JSON.stringify({
      name: role.name,
      id: role.id,
      permissions: normalizePermissions(role.permissions ?? [])
    })
  );
};

export const readStoredPermissions = (): PermissionType[] => {
  try {
    const rolePermissions = normalizePermissions(readStoredRole()?.permissions ?? []);
    if (rolePermissions.length) {
      return rolePermissions;
    }

    const raw = localStorage.getItem(STORAGE_KEYS.ROLE_PERMISSIONS);
    if (raw) {
      const parsed = JSON.parse(raw);
      return normalizePermissions(parsed);
    }

    return [];
  } catch {
    return normalizePermissions(readStoredRole()?.permissions ?? []);
  }
};
